<ng-container>

    <!-- Top action bar -->
    <div class="mb-3" aria-busy="true" aria-label="Loading assessment details">
      <div class="d-flex justify-content-between align-items-center">
        <!-- Back button -->
        <div class="skl-btn-back-placeholder"></div>
        <!-- Action buttons -->
        <div class="d-flex gap-2">
          <app-skeleton width="140px" height="34px" borderRadius="4px" marginBottom="0"></app-skeleton>
          <app-skeleton width="70px" height="34px" borderRadius="4px" marginBottom="0"></app-skeleton>
          <app-skeleton width="100px" height="34px" borderRadius="4px" marginBottom="0"></app-skeleton>
        </div>
      </div>
    </div>

    <!-- Application Information (As-Is) — white card with 4 rows × 3 cells -->
    <div class="skl-app-info-section mb-3">
      <div class="skl-analysis-header skl-analysis-header--blue">
        <app-skeleton width="240px" height="14px" borderRadius="3px" marginBottom="0" class="skl-header-bar-white"></app-skeleton>
      </div>
      <div class="skl-info-grid">
        @for (row of infoRows; track row) {
          <div class="skl-info-row" [class.skl-info-row--last]="row === infoRows.length">
            @for (cell of analysisCells; track cell) {
              <div class="skl-info-cell">
                <app-skeleton width="60%" height="17px" borderRadius="3px" marginBottom="0"></app-skeleton>
                <app-skeleton width="80%" height="20px" borderRadius="3px" marginBottom="0"></app-skeleton>
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Two columns: CMDB Analysis + assessment summary -->
    <div class="row g-3 mb-3">
      <!-- CMDB Analysis card -->
      <div class="col-12 col-lg-6">
        <div class="skl-analysis-card">
          <div class="skl-analysis-header skl-analysis-header--blue">
            <app-skeleton width="200px" height="14px" borderRadius="3px" marginBottom="0" class="skl-header-bar-white"></app-skeleton>
          </div>
          <div class="skl-analysis-content">
            <!-- 3 analysis items row -->
            <div class="skl-analysis-row mb-3">
              @for (cell of analysisCells; track cell) {
                <div class="skl-analysis-item">
                  <app-skeleton width="65%" height="11px" borderRadius="3px" marginBottom="6px"></app-skeleton>
                  <app-skeleton width="80%" height="14px" borderRadius="3px" marginBottom="0"></app-skeleton>
                </div>
              }
            </div>
            <!-- Rationale textbox -->
            <div>
              <app-skeleton width="60px" height="11px" borderRadius="3px" marginBottom="6px"></app-skeleton>
              <app-skeleton width="100%" height="80px" borderRadius="4px" marginBottom="0"></app-skeleton>
            </div>
          </div>
        </div>
      </div>

      <!-- Assessment summary card -->
      <div class="col-12 col-lg-6">
        <div class="skl-analysis-card">
          <div class="skl-analysis-content">
            <app-skeleton width="150px" height="14px" borderRadius="3px" marginBottom="14px"></app-skeleton>
            <app-skeleton width="100%" height="12px" borderRadius="3px" marginBottom="10px"></app-skeleton>
            <app-skeleton width="85%" height="12px" borderRadius="3px" marginBottom="10px"></app-skeleton>
            <app-skeleton width="100%" height="12px" borderRadius="3px" marginBottom="16px"></app-skeleton>
            <app-skeleton width="190px" height="14px" borderRadius="3px" marginBottom="10px"></app-skeleton>
            <app-skeleton width="100%" height="64px" borderRadius="4px" marginBottom="0"></app-skeleton>
          </div>
        </div>
      </div>
    </div>

    <!-- Full-width source-code assessment card -->
    <div class="skl-analysis-card mb-3">
      <div class="skl-analysis-header skl-analysis-header--blue">
        <app-skeleton width="210px" height="14px" borderRadius="3px" marginBottom="0" class="skl-header-bar-white"></app-skeleton>
      </div>
      <div class="skl-analysis-content">
        <div class="skl-analysis-row mb-3">
          @for (cell of analysisCells; track cell) {
            <div class="skl-analysis-item">
              <app-skeleton width="65%" height="11px" borderRadius="3px" marginBottom="6px"></app-skeleton>
              <app-skeleton width="80%" height="14px" borderRadius="3px" marginBottom="0"></app-skeleton>
            </div>
          }
        </div>
        <app-skeleton width="60px" height="11px" borderRadius="3px" marginBottom="6px"></app-skeleton>
        <app-skeleton width="100%" height="80px" borderRadius="4px" marginBottom="0"></app-skeleton>
      </div>
    </div>

    <!-- Code Assessment Details section -->
    <div class="skl-details-section">
      <div class="skl-analysis-header skl-analysis-header--blue mb-0">
        <app-skeleton width="270px" height="14px" borderRadius="3px" marginBottom="0" class="skl-header-bar-white"></app-skeleton>
      </div>
      <div class="skl-details-body row g-4">
        <!-- Left: dimension table (col-lg-7) -->
        <div class="col-12 col-lg-7">
          <!-- Table header -->
          <div class="skl-dim-header-row">
            <app-skeleton width="80px" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
            <app-skeleton width="40px" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
            <app-skeleton width="130px" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
          </div>
          @for (row of dimensionRows; track row) {
            <div class="skl-dim-row">
              <app-skeleton width="80%" height="13px" borderRadius="3px" marginBottom="0"></app-skeleton>
              <app-skeleton width="55px" height="22px" borderRadius="10px" marginBottom="0"></app-skeleton>
              <div class="skl-dim-reasoning">
                <app-skeleton width="100%" height="12px" borderRadius="3px" marginBottom="5px"></app-skeleton>
                <app-skeleton width="80%" height="12px" borderRadius="3px" marginBottom="0"></app-skeleton>
              </div>
            </div>
          }
        </div>
        <!-- Right: radar chart + scope (col-lg-5) -->
        <div class="col-12 col-lg-5">
          <div class="skl-radar-wrap">
            <div class="skl-radar-circle"></div>
          </div>
          <div class="mt-4">
            <app-skeleton width="220px" height="14px" borderRadius="3px" marginBottom="10px"></app-skeleton>
            @for (row of [1,2,3]; track row) {
              <app-skeleton width="95%" height="12px" borderRadius="3px" marginBottom="7px"></app-skeleton>
            }
          </div>
        </div>
      </div>
    </div>

</ng-container>
